/**
 * Hero card showcase — horizontal card track with mobile nav.
 */
class HeroCardShowcase extends HTMLElement {
  constructor() {
    super();
    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onPrevClick = this._onPrevClick.bind(this);
    this._onNextClick = this._onNextClick.bind(this);
    this._onPrevKeydown = this._onPrevKeydown.bind(this);
    this._onNextKeydown = this._onNextKeydown.bind(this);
    /** @type {ResizeObserver | null} */
    this.resizeObserver = null;
  }

  connectedCallback() {
    this._unbind();

    this.cardsWrap = this.querySelector('[data-hero-card-cards]');
    this.viewport = this.querySelector('[data-hero-card-viewport]');
    this.track = this.querySelector('[data-hero-card-track]');
    this.nav = this.querySelector('[data-hero-card-nav]');
    this.prevButton = this.querySelector('[data-hero-card-prev]');
    this.nextButton = this.querySelector('[data-hero-card-next]');
    this.showMobileNav = this.dataset.showMobileNav === 'true';

    if (!this.cardsWrap || !this.viewport || !this.track) return;

    this.viewport.addEventListener('scroll', this._onScroll, { passive: true });
    this.prevButton?.addEventListener('click', this._onPrevClick);
    this.nextButton?.addEventListener('click', this._onNextClick);
    this.prevButton?.addEventListener('keydown', this._onPrevKeydown);
    this.nextButton?.addEventListener('keydown', this._onNextKeydown);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this._updateLayoutState());
      this.resizeObserver.observe(this.viewport);
      this.resizeObserver.observe(this.track);
    }

    window.addEventListener('resize', this._onResize);
    this._updateLayoutState();
  }

  disconnectedCallback() {
    this._unbind();
  }

  _unbind() {
    this.viewport?.removeEventListener('scroll', this._onScroll);
    this.prevButton?.removeEventListener('click', this._onPrevClick);
    this.nextButton?.removeEventListener('click', this._onNextClick);
    this.prevButton?.removeEventListener('keydown', this._onPrevKeydown);
    this.nextButton?.removeEventListener('keydown', this._onNextKeydown);
    window.removeEventListener('resize', this._onResize);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  _onScroll() {
    this._updateScrollState();
  }

  _onResize() {
    this._updateLayoutState();
  }

  _onPrevClick() {
    this._handleNavActivate(this.prevButton, -1);
  }

  _onNextClick() {
    this._handleNavActivate(this.nextButton, 1);
  }

  /**
   * @param {KeyboardEvent} event
   */
  _onPrevKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this._handleNavActivate(this.prevButton, -1);
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  _onNextKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this._handleNavActivate(this.nextButton, 1);
    }
  }

  /**
   * @returns {number}
   */
  _getScrollStep() {
    const firstCard = this.track.children[0];
    if (!firstCard) return this.viewport.clientWidth;

    const trackStyles = window.getComputedStyle(this.track);
    const gap = parseFloat(trackStyles.columnGap || trackStyles.gap || '0') || 0;
    return firstCard.offsetWidth + gap;
  }

  /**
   * @returns {number}
   */
  _getActiveCardIndex() {
    const cards = Array.from(this.track.children);
    if (!cards.length) return 0;

    const scrollLeft = this.viewport.scrollLeft;
    let activeIndex = 0;
    let minDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      const distance = Math.abs(card.offsetLeft - scrollLeft);
      if (distance < minDistance) {
        minDistance = distance;
        activeIndex = index;
      }
    });

    return activeIndex;
  }

  /**
   * @param {Element | null} button
   * @param {boolean} isDisabled
   */
  _setNavDisabled(button, isDisabled) {
    if (!button) return;
    button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
    button.classList.toggle('swiper-button-disabled', isDisabled);
  }

  _updateLayoutState() {
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    const isOverflowing = this.track.scrollWidth > this.viewport.clientWidth + 1;
    const fitsDesktop = isDesktop && !isOverflowing;
    const cardCount = this.track.children.length;

    this.track.classList.toggle('hero-card-showcase__track--align-center', fitsDesktop);
    this.track.classList.toggle('hero-card-showcase__track--fits', fitsDesktop);

    if (this.nav) {
      const showNav = this.showMobileNav && !isDesktop && cardCount > 1;
      this.nav.classList.toggle('is-visible', showNav);
    }

    this._updateScrollState();
  }

  _updateScrollState() {
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    const cardCount = this.track.children.length;

    if (!isDesktop && cardCount > 1) {
      const activeIndex = this._getActiveCardIndex();
      this._setNavDisabled(this.prevButton, activeIndex <= 0);
      this._setNavDisabled(this.nextButton, activeIndex >= cardCount - 1);
      return;
    }

    const maxScrollLeft = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    const hasScrolled = this.viewport.scrollLeft > 4;
    const atEnd = this.viewport.scrollLeft >= maxScrollLeft - 4;

    this._setNavDisabled(this.prevButton, !hasScrolled);
    this._setNavDisabled(this.nextButton, atEnd);
  }

  /**
   * @param {number} direction
   */
  _scrollByStep(direction) {
    this.viewport.scrollBy({ left: direction * this._getScrollStep(), behavior: 'smooth' });
  }

  /**
   * @param {Element | null} button
   * @param {number} direction
   */
  _handleNavActivate(button, direction) {
    if (!button || button.getAttribute('aria-disabled') === 'true') return;
    this._scrollByStep(direction);
  }
}

if (!customElements.get('hero-card-showcase')) {
  customElements.define('hero-card-showcase', HeroCardShowcase);
}
